import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, Globe } from "lucide-react";
import { COMPANY } from "../data/mock";
import { useToast } from "../hooks/use-toast";

const INITIAL = { name: "", company: "", email: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      toast({
        title: "Request received!",
        description: `Thanks ${form.name || ""} \u2014 a team member will reach out shortly.`,
      });
      setForm(INITIAL);
    }, 700);
  };

  const contactCards = [
    { icon: MapPin, label: "Address", value: COMPANY.address },
    { icon: Phone, label: "Phone", value: COMPANY.phone, href: COMPANY.phoneHref },
    { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { icon: Globe, label: "Website", value: COMPANY.website },
  ];

  return (
    <main>
      <section className="bg-[var(--brand-hero)]">
        <div className="mx-auto max-w-4xl px-5 py-16 text-center lg:py-20 lg:px-8">
          <span className="text-sm font-bold uppercase tracking-wider text-teal-brand">Get in Touch</span>
          <h1 className="mt-3 text-4xl font-extrabold text-navy-brand sm:text-5xl">Request a Quote</h1>
          <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed text-slate-500">
            Tell us about your inventory and one of our buyers will follow up with a free, no-obligation
            consultation. We are available 24/7.
          </p>
        </div>
      </section>

      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 lg:grid-cols-5 lg:px-8">
        {/* Info */}
        <div className="lg:col-span-2">
          <h2 className="text-2xl font-extrabold text-navy-brand">Contact information</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">
            Reach us by phone, email or stop by our Manhattan office. We respond to every inquiry quickly.
          </p>
          <div className="mt-8 space-y-4">
            {contactCards.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.label} className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-teal-brand/10 text-teal-brand">
                    <Icon size={22} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{c.label}</p>
                    {c.href ? (
                      <a href={c.href} className="font-medium text-navy-brand hover:text-teal-brand">{c.value}</a>
                    ) : (
                      <p className="font-medium text-navy-brand">{c.value}</p>
                    )}
                  </div>
                </div>
              );
            })}
            <div className="flex items-start gap-4 rounded-2xl bg-navy-brand p-5 text-white">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10">
                <Clock size={22} className="text-teal-brand" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">Hours</p>
                <p className="font-medium">24 hours a day, 7 days a week</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-3">
          <form onSubmit={submit} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm lg:p-10">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Full Name" required value={form.name} onChange={update("name")} placeholder="John Smith" />
              <Field label="Company" value={form.company} onChange={update("company")} placeholder="Your company" />
              <Field label="Email" type="email" required value={form.email} onChange={update("email")} placeholder="you@company.com" />
              <Field label="Phone" value={form.phone} onChange={update("phone")} placeholder="(000) 000-0000" />
            </div>
            <div className="mt-5">
              <label className="mb-1.5 block text-sm font-semibold text-navy-brand">Tell us about your inventory *</label>
              <textarea
                required
                value={form.message}
                onChange={update("message")}
                rows={5}
                placeholder="Product categories, approximate quantity / pallets, condition, location..."
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition-colors focus:border-teal-brand"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-navy-brand px-8 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-teal-brand disabled:opacity-60"
            >
              {submitting ? "Sending..." : "Send Request"}
              <Send size={16} />
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}

function Field({ label, type = "text", required, value, onChange, placeholder }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-semibold text-navy-brand">
        {label} {required && "*"}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition-colors focus:border-teal-brand"
      />
    </div>
  );
}
